import {
  ErrorPresentation,
  standardDescriptionValidation,
  standardTitleValidation,
  STANDARD_DESCRIPTION_MAXIMUM,
  STANDARD_TITLE_MAXIMUM,
  STANDARD_TITLE_MINIMUM,
} from 'helpers/validators';
import { getRichTextLength, richTextToPlainText } from 'shared/utils/richText';
import { FieldSize } from 'shared/types/fieldSize';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { CommonTranslationKeys, ValidationTranslationKeys } from 'intl/keys';
import {
  ChecklistAnswerError,
  ChecklistItemError,
  ChecklistItemValidationFocus,
  CreateInspectionTemplateErrors,
  FormChecklistAnswer,
  FormChecklistItem,
  FormSection,
  SectionError,
} from './types';

const ERROR_VALIDATION_REQUIRED = { helperText: { id: CommonTranslationKeys.required } };
const ERROR_NUMERIC_MIN_MAX_ORDER = {
  helperText: { id: ValidationTranslationKeys.numericMinMaxOrder },
};
const ERROR_NUMERIC_SLIDER_ALL_OR_NONE = {
  helperText: { id: ValidationTranslationKeys.numericSliderAllOrNone },
};
const ERROR_NUMERIC_STEP_POSITIVE = {
  helperText: { id: ValidationTranslationKeys.numericStepPositive },
};
const ERROR_NUMERIC_STEP_EXCEEDS_RANGE = {
  helperText: { id: ValidationTranslationKeys.numericStepExceedsRange },
};
const ERROR_STANDARD_TITLE_LENGTH = {
  helperText: {
    id: ValidationTranslationKeys.stringLengthBetween,
  },
  values: {
    minLength: STANDARD_TITLE_MINIMUM,
    maxLength: STANDARD_TITLE_MAXIMUM,
  },
};
const ERROR_STANDARD_DESCRIPTION_LENGTH = {
  helperText: {
    id: ValidationTranslationKeys.stringLengthMaximumWeb,
  },
  values: { maxLength: STANDARD_DESCRIPTION_MAXIMUM },
};
const ERROR_RICH_TEXT_DESCRIPTION_LENGTH = {
  helperText: {
    id: ValidationTranslationKeys.stringLengthMaximumWeb,
  },
  values: { maxLength: FieldSize.RICH_TEXT },
};

export function summaryValidation(summary: string): ErrorPresentation {
  if (!summary) {
    return ERROR_VALIDATION_REQUIRED;
  } else if (standardTitleValidation(summary)) {
    return ERROR_STANDARD_TITLE_LENGTH;
  }

  return undefined;
}

export function titleValidation(title: string): ErrorPresentation {
  if (!title) {
    return ERROR_VALIDATION_REQUIRED;
  } else if (standardTitleValidation(title)) {
    return ERROR_STANDARD_TITLE_LENGTH;
  }

  return undefined;
}

export function processValidation(
  process: string | null
): ErrorPresentation {
  return !process ? ERROR_VALIDATION_REQUIRED : undefined;
}

export function descriptionValidation(
  description: string
): ErrorPresentation {
  // For rich text HTML content, use plain text length validation
  const plainTextLength = getRichTextLength(description);
  return plainTextLength > FieldSize.RICH_TEXT
    ? ERROR_RICH_TEXT_DESCRIPTION_LENGTH
    : undefined;
}

export function answerLabelValidation(label: string): ErrorPresentation {
  return summaryValidation(label);
}

/**
 * An `OPTION_SELECTION` option label is one line of rich text (PT-7250): its
 * visible text is required and limited like a title; tags do not count.
 */
export function optionLabelValidation(label: string): ErrorPresentation {
  return summaryValidation(richTextToPlainText(label));
}

function complianceOptionLabelValidation(label: string): ErrorPresentation {
  const trimmed = label.trim();
  if (!trimmed) {
    return undefined;
  }

  return standardTitleValidation(trimmed)
    ? ERROR_STANDARD_TITLE_LENGTH
    : undefined;
}

function validateNumericAnswer(
  answer: FormChecklistAnswer & { type: AnswerType.Numeric }
): Pick<
  ChecklistAnswerError,
  'normMin' | 'normMax' | 'rangeMin' | 'rangeMax' | 'step'
> {
  const result: Pick<
    ChecklistAnswerError,
    'normMin' | 'normMax' | 'rangeMin' | 'rangeMax' | 'step'
  > = {};

  if (
    answer.normMin != null &&
    answer.normMax != null &&
    answer.normMin > answer.normMax
  ) {
    result.normMin = ERROR_NUMERIC_MIN_MAX_ORDER;
    result.normMax = ERROR_NUMERIC_MIN_MAX_ORDER;
  }

  if (
    answer.rangeMin != null &&
    answer.rangeMax != null &&
    answer.rangeMin > answer.rangeMax
  ) {
    result.rangeMin = ERROR_NUMERIC_MIN_MAX_ORDER;
    result.rangeMax = ERROR_NUMERIC_MIN_MAX_ORDER;
  }

  const { step, rangeMin, rangeMax } = answer;
  const hasAnySliderField =
    step != null || rangeMin != null || rangeMax != null;
  const hasAllSliderFields =
    step != null && rangeMin != null && rangeMax != null;

  if (hasAnySliderField && !hasAllSliderFields) {
    if (step == null) {
      result.step = ERROR_NUMERIC_SLIDER_ALL_OR_NONE;
    }
    if (rangeMin == null) {
      result.rangeMin = ERROR_NUMERIC_SLIDER_ALL_OR_NONE;
    }
    if (rangeMax == null) {
      result.rangeMax = ERROR_NUMERIC_SLIDER_ALL_OR_NONE;
    }
    return result;
  }

  if (hasAllSliderFields) {
    if (step <= 0) {
      result.step = ERROR_NUMERIC_STEP_POSITIVE;
    } else if (step > rangeMax - rangeMin) {
      result.step = ERROR_NUMERIC_STEP_EXCEEDS_RANGE;
    }
  }

  return result;
}

function validateSingleChecklistAnswer(
  answer: FormChecklistAnswer
): ChecklistAnswerError {
  const result: ChecklistAnswerError = {
    label: answerLabelValidation(answer.label),
  };

  if (answer.type === AnswerType.Numeric) {
    Object.assign(result, validateNumericAnswer(answer));
  }

  if (answer.type === AnswerType.OptionSelection) {
    const options: Record<string, ErrorPresentation> = {};
    answer.options.forEach((option) => {
      const optionError = optionLabelValidation(option.label);
      if (optionError) {
        options[option._id] = optionError;
      }
    });
    if (Object.keys(options).length > 0) {
      result.options = options;
    }
  }

  if (answer.type === AnswerType.Compliance) {
    const optionLabels: Partial<
      Record<ComplianceAnswerOption, ErrorPresentation>
    > = {};
    answer.optionLabels
      .filter((optionLabel) => optionLabel.option !== ComplianceAnswerOption.unset)
      .forEach((optionLabel) => {
        const optionError = complianceOptionLabelValidation(optionLabel.label);
        if (optionError) {
          optionLabels[optionLabel.option] = optionError;
        }
      });
    if (Object.keys(optionLabels).length > 0) {
      result.optionLabels = optionLabels;
    }
  }

  return result;
}

export function validateFormChecklistAnswers(
  answers: FormChecklistAnswer[]
): ChecklistAnswerError[] {
  return answers.map(validateSingleChecklistAnswer);
}

/** Pairs parallel answer/error arrays by answer `_id` or `uniqueKeyId`. */
export function checklistAnswerErrorsByAnswerId(
  answers: readonly FormChecklistAnswer[],
  answerErrors?: ChecklistAnswerError[]
): Map<string, ChecklistAnswerError> {
  const errorsByAnswerId = new Map<string, ChecklistAnswerError>();

  if (!answerErrors?.length) {
    return errorsByAnswerId;
  }

  answers.forEach((answer, index) => {
    const answerKey = answer._id || answer.uniqueKeyId;
    const fieldErrors = answerErrors[index];

    if (fieldErrors !== undefined) {
      errorsByAnswerId.set(answerKey, fieldErrors);
    }
  });

  return errorsByAnswerId;
}

function hasChecklistAnswerError(error: ChecklistAnswerError): boolean {
  if (error.label) {
    return true;
  }

  if (error.normMin || error.normMax || error.rangeMin || error.rangeMax || error.step) {
    return true;
  }

  if (error.options && Object.values(error.options).some(Boolean)) {
    return true;
  }

  if (error.optionLabels && Object.values(error.optionLabels).some(Boolean)) {
    return true;
  }

  return false;
}

export function hasAnswersTabErrors(
  answerErrors?: ChecklistAnswerError[]
): boolean {
  return Boolean(answerErrors?.some(hasChecklistAnswerError));
}

export function hasBasicTabErrors(itemError: ChecklistItemError): boolean {
  return Boolean(itemError.summary || itemError.description);
}

export function findFirstChecklistValidationFocus(
  items: FormChecklistItem[],
  itemErrors: ChecklistItemError[]
): ChecklistItemValidationFocus | null {
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const itemError = itemErrors[index];
    if (!item || !itemError) {
      continue;
    }

    const itemId = item._id || item.uniqueKeyId;

    if (hasBasicTabErrors(itemError)) {
      return { itemId, tab: 'basic' };
    }

    if (hasAnswersTabErrors(itemError.answers)) {
      return { itemId, tab: 'answers' };
    }
  }

  return null;
}

function areChecklistAnswerErrorsEqual(
  prev?: ChecklistAnswerError,
  curr?: ChecklistAnswerError
): boolean {
  if (!prev && !curr) {
    return true;
  }

  if (!prev || !curr) {
    return false;
  }

  if (prev.label?.helperText.id !== curr.label?.helperText.id) {
    return false;
  }

  const prevOptionIds = Object.keys(prev.options ?? {});
  const currOptionIds = Object.keys(curr.options ?? {});
  if (prevOptionIds.length !== currOptionIds.length) {
    return false;
  }
  if (
    prevOptionIds.some(
      (optionId) =>
        prev.options?.[optionId]?.helperText.id !==
        curr.options?.[optionId]?.helperText.id
    )
  ) {
    return false;
  }

  const prevComplianceOptions = Object.keys(prev.optionLabels ?? {});
  const currComplianceOptions = Object.keys(curr.optionLabels ?? {});
  if (prevComplianceOptions.length !== currComplianceOptions.length) {
    return false;
  }

  return !prevComplianceOptions.some((option) => {
    const prevOption = option as ComplianceAnswerOption;
    return (
      prev.optionLabels?.[prevOption]?.helperText.id !==
      curr.optionLabels?.[prevOption]?.helperText.id
    );
  });
}

function areChecklistAnswerErrorsListEqual(
  prev?: ChecklistAnswerError[],
  curr?: ChecklistAnswerError[]
): boolean {
  const prevErrors = prev ?? [];
  const currErrors = curr ?? [];

  if (prevErrors.length !== currErrors.length) {
    return false;
  }

  return prevErrors.every((prevError, index) =>
    areChecklistAnswerErrorsEqual(prevError, currErrors[index])
  );
}

function identicalErrors(
  prev: CreateInspectionTemplateErrors,
  curr: CreateInspectionTemplateErrors
): boolean {
  return (
    curr.description?.helperText.id === prev.description?.helperText.id &&
    curr.summary?.helperText.id === prev.summary?.helperText.id &&
    curr.process?.helperText.id === prev.process?.helperText.id
  );
}

export function chooseOptimalErrors(
  prev: CreateInspectionTemplateErrors,
  curr: CreateInspectionTemplateErrors
): CreateInspectionTemplateErrors {
  return identicalErrors(prev, curr) ? prev : curr;
}

function identicalChecklistError(
  prev: ChecklistItemError,
  curr: ChecklistItemError
): boolean {
  return (
    curr.description?.helperText.id === prev.description?.helperText.id &&
    curr.summary?.helperText.id === prev.summary?.helperText.id &&
    areChecklistAnswerErrorsListEqual(prev.answers, curr.answers)
  );
}

export function chooseOptimalChecklistErrors(
  checklist: FormChecklistItem[],
  checklistErrors: ChecklistItemError[]
): FormChecklistItem[] {
  const indexes = checklist.reduce<number[]>(
    (indexesWithChange, item: FormChecklistItem, index: number) => {
      if (!identicalChecklistError(item.errors, checklistErrors[index])) {
        indexesWithChange.push(index);
      }
      return indexesWithChange;
    },
    []
  );

  if (!indexes.length) {
    return checklist;
  }

  return checklist.map((checklistItem, index) => {
    if (indexes.includes(index)) {
      return {
        ...checklistItem,
        errors: checklistErrors[index],
      };
    }
    return checklistItem;
  });
}

function identicalSectionError(
  prev: SectionError,
  curr: SectionError
): boolean {
  return (
    curr.description?.helperText.id === prev.description?.helperText.id &&
    curr.title?.helperText.id === prev.title?.helperText.id
  );
}

export function chooseOptimalSectionErrors(
  sections: FormSection[],
  sectionErrors: SectionError[]
): FormSection[] {
  const indexes = sections.reduce<number[]>(
    (indexesWithChange, section: FormSection, index: number) => {
      if (!identicalSectionError(section.errors, sectionErrors[index])) {
        indexesWithChange.push(index);
      }
      return indexesWithChange;
    },
    []
  );

  if (!indexes.length) {
    return sections;
  }

  return sections.map((section, index) => {
    if (indexes.includes(index)) {
      return {
        ...section,
        errors: sectionErrors[index],
      };
    }
    return section;
  });
}
